import React, { useState, useEffect } from 'react';
import './AdminDebugPanel.css';
import MapsTestPage from './MapsTestPage';

// Performance Debug Panel Component
const PerformanceDebugPanel = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [thresholds, setThresholds] = useState({
    frequency: 5, // Alert if more than 5 events per second
    memory: 100, // Alert if memory usage grows by more than 100MB
    time: 1000 // Alert if operations take longer than 1 second
  });
  const [lastCleared, setLastCleared] = useState(new Date().toISOString());
  
  useEffect(() => {
    let interval;
    
    if (isMonitoring) {
      // Monitor performance every 100ms when active
      interval = setInterval(() => {
        const now = Date.now();
        const entry = {
          timestamp: now,
          memory: performance.memory ? {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
          } : null,
          timing: {
            navigation: performance.getEntriesByType('navigation')[0],
            paint: performance.getEntriesByType('paint'),
            measure: performance.getEntriesByType('measure').slice(-10) // Last 10 measures
          }
        };
        
        setPerformanceData(prev => {
          const newData = [entry, ...prev].slice(0, 600); // Keep last 60 seconds worth of data
          
          // Check for high-frequency events (potential flickering)
          const recentData = newData.filter(d => (now - d.timestamp) < 1000); // Last 1 second
          if (recentData.length > thresholds.frequency) {
            console.warn('⚠️ HIGH FREQUENCY UPDATES DETECTED:', recentData.length, 'updates in last second');
            if (window.adminDebug) {
              window.adminDebug.addLog(`HIGH FREQUENCY: ${recentData.length} updates/sec`, 'warning', {
                count: recentData.length,
                threshold: thresholds.frequency
              });
            }
          }
          
          return newData;
        });
      }, 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMonitoring, thresholds.frequency]);
  
  // Monitor React re-renders by watching for DOM changes
  useEffect(() => {
    if (!isMonitoring) return;
    
    let renderCount = 0;
    let lastRenderTime = Date.now();
    
    const observer = new MutationObserver((mutations) => {
      const now = Date.now();
      renderCount++;
      
      // Check for rapid DOM mutations (sign of flickering)
      if (now - lastRenderTime < 100) { // Less than 100ms since last render
        console.warn('⚠️ RAPID DOM CHANGES DETECTED');
        if (window.adminDebug) {
          window.adminDebug.addLog('RAPID DOM CHANGES: Potential flickering detected', 'warning', {
            mutations: mutations.length,
            timeSinceLastRender: now - lastRenderTime
          });
        }
      }
      
      lastRenderTime = now;
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true
    });
    
    return () => observer.disconnect();
  }, [isMonitoring]);
  
  const clearPerformanceData = () => {
    setPerformanceData([]);
    setLastCleared(new Date().toISOString());
    if (window.adminDebug) {
      window.adminDebug.addLog('Performance monitoring data cleared', 'system');
    }
  };
  
  const detectPerformanceIssues = () => {
    const now = Date.now();
    const issues = [];
    
    // Check for high frequency updates in last 5 seconds
    const recent = performanceData.filter(d => (now - d.timestamp) < 5000);
    if (recent.length > thresholds.frequency * 5) {
      issues.push(`High update frequency: ${Math.round(recent.length / 5)} updates/sec (threshold: ${thresholds.frequency}/sec)`);
    }
    
    // Check memory growth
    if (performanceData.length > 10) {
      const current = performanceData[0]?.memory?.used || 0;
      const past = performanceData[Math.min(100, performanceData.length - 1)]?.memory?.used || 0;
      const growth = current - past;
      
      if (growth > thresholds.memory) {
        issues.push(`Memory growth: +${growth}MB (threshold: ${thresholds.memory}MB)`);
      }
    }
    
    return issues;
  };
  
  const exportPerformanceData = () => {
    const dataToExport = {
      timestamp: new Date().toISOString(),
      monitoringPeriod: {
        start: lastCleared,
        end: new Date().toISOString(),
        duration: `${Math.round((Date.now() - new Date(lastCleared).getTime()) / 1000)}s`
      },
      performanceData: performanceData.slice(0, 100), // Export last 100 entries
      issues: detectPerformanceIssues(),
      thresholds,
      summary: {
        totalEntries: performanceData.length,
        averageMemory: performanceData.length > 0 
          ? Math.round(performanceData.reduce((sum, d) => sum + (d.memory?.used || 0), 0) / performanceData.length)
          : 0,
        currentMemory: performanceData[0]?.memory?.used || 0
      }
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const issues = detectPerformanceIssues();
  const currentMemory = performanceData[0]?.memory;
  const avgUpdateFreq = performanceData.length > 0 
    ? Math.round(performanceData.filter(d => (Date.now() - d.timestamp) < 1000).length)
    : 0;
  
  return (
    <div className="performance-debug-panel">
      <div className="panel-header">
        <h3>⚡ Performance Monitor</h3>
        <div className="debug-controls">
          <button 
            onClick={() => setIsMonitoring(!isMonitoring)} 
            className={`btn-debug ${isMonitoring ? 'btn-stop' : 'btn-start'}`}
          >
            {isMonitoring ? '⏹️ Stop' : '▶️ Start'} Monitoring
          </button>
          <button onClick={clearPerformanceData} className="btn-debug btn-clear">
            🗑️ Clear Data
          </button>
          <button onClick={exportPerformanceData} disabled={performanceData.length === 0} className="btn-debug btn-export">
            📁 Export
          </button>
        </div>
      </div>
      
      <div className="debug-info">
        <div className="info-row">
          <strong>Monitoring:</strong> {isMonitoring ? '🟢 Active' : '🔴 Stopped'}
        </div>
        <div className="info-row">
          <strong>Data Points:</strong> {performanceData.length}
        </div>
        <div className="info-row">
          <strong>Current Update Rate:</strong> {avgUpdateFreq}/sec
        </div>
        <div className="info-row">
          <strong>Memory Usage:</strong> {currentMemory ? `${currentMemory.used}MB / ${currentMemory.total}MB` : 'N/A'}
        </div>
        <div className="info-row">
          <strong>Issues Detected:</strong> {issues.length}
        </div>
      </div>
      
      <div className="threshold-controls">
        <h4>⚙️ Thresholds</h4>
        <div className="threshold-grid">
          <div className="threshold-item">
            <label>Update Frequency (per sec):</label>
            <input 
              type="number" 
              value={thresholds.frequency} 
              onChange={(e) => setThresholds(prev => ({ ...prev, frequency: parseInt(e.target.value) }))}
              min="1"
              max="100"
            />
          </div>
          <div className="threshold-item">
            <label>Memory Growth (MB):</label>
            <input 
              type="number" 
              value={thresholds.memory} 
              onChange={(e) => setThresholds(prev => ({ ...prev, memory: parseInt(e.target.value) }))}
              min="10"
              max="1000"
            />
          </div>
        </div>
      </div>
      
      {issues.length > 0 && (
        <div className="performance-issues">
          <h4>⚠️ Performance Issues</h4>
          {issues.map((issue, index) => (
            <div key={index} className="issue-item">
              ❌ {issue}
            </div>
          ))}
        </div>
      )}
      
      {performanceData.length > 0 && (
        <div className="performance-chart">
          <h4>📊 Recent Activity (Last 10 seconds)</h4>
          <div className="chart-container">
            {performanceData
              .filter(d => (Date.now() - d.timestamp) < 10000)
              .slice(0, 100)
              .map((data, index) => {
                const height = Math.min(100, (data.memory?.used || 0) / 2); // Scale memory to chart height
                const color = avgUpdateFreq > thresholds.frequency ? '#ff4444' : '#44ff44';
                return (
                  <div 
                    key={index} 
                    className="chart-bar" 
                    style={{ 
                      height: `${height}px`, 
                      backgroundColor: color,
                      width: '2px',
                      marginRight: '1px',
                      display: 'inline-block'
                    }}
                    title={`Memory: ${data.memory?.used || 0}MB at ${new Date(data.timestamp).toLocaleTimeString()}`}
                  />
                );
              })}
          </div>
          <div className="chart-legend">
            <span>🟢 Normal | 🔴 High Frequency</span>
          </div>
        </div>
      )}
      
      <div className="monitoring-help">
        <h4>ℹ️ What This Monitors</h4>
        <ul>
          <li><strong>Update Frequency:</strong> Detects rapid state changes that cause flickering</li>
          <li><strong>Memory Usage:</strong> Tracks memory leaks and excessive allocations</li>
          <li><strong>DOM Changes:</strong> Monitors for rapid DOM mutations (re-renders)</li>
          <li><strong>Performance Timing:</strong> Tracks navigation and paint events</li>
        </ul>
        <p><strong>Usage:</strong> Start monitoring, reproduce the flickering issue, then review the data and alerts.</p>
      </div>
    </div>
  );
};

// Notification Debug Panel Component
const OpenAIDebugPanel = ({ user, supabaseHelpers }) => {
  const [llmLogs, setLlmLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testResponse, setTestResponse] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  const loadLLMLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseHelpers.getLLMProcessingLogs(user?.id, 50);
      if (error) {
        console.error('Error loading LLM logs:', error);
        return;
      }
      setLlmLogs(data || []);
    } catch (error) {
      console.error('Error loading LLM logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const testOpenAIAPI = async () => {
    setTestLoading(true);
    setTestResponse('');
    
    try {
      const testPrompt = "I love trying new Thai restaurants and coffee shops.";
      
      // This will simulate what the onboarding modal does
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
        setTestResponse('❌ OpenAI API key not configured in environment variables');
        return;
      }

      const requestData = {
        model: "gpt-4-turbo-preview",
        messages: [{ 
          role: "user", 
          content: `You are an expert at mapping user interests to Google Places API types.

Context: User is answering a ideal_saturday question.
Focus on leisure, entertainment, recreation, outdoor activities, dining, and shopping venues where Saturday activities happen.

User response: "${testPrompt}"

Available Google Places types: restaurant, cafe, thai_restaurant, coffee_shop, bar, etc.

Extract relevant information and respond with ONLY valid JSON:
{
  "category_tags": ["human-readable interest tags"],
  "google_places_categories": ["valid_google_places_types"],
  "confidence_score": 0.0-1.0
}

Rules:
- Only use Google Places types from the provided list
- Maximum 5 category_tags and 5 google_places_categories
- Confidence score based on clarity and specificity of user response`
        }],
        temperature: 0.1,
        max_tokens: 500,
        response_format: { type: "json_object" }
      };

      const startTime = Date.now();
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(requestData)
      });

      const duration = Date.now() - startTime;
      
      if (!response.ok) {
        const errorData = await response.text();
        setTestResponse(`❌ API Error (${response.status}): ${errorData}`);
        return;
      }

      const responseData = await response.json();
      
      // Add to admin debug panel's API calls
      if (window.adminDebug?.addApiCall) {
        window.adminDebug.addApiCall(
          'https://api.openai.com/v1/chat/completions',
          'POST',
          requestData,
          responseData,
          duration
        );
      }

      const result = {
        success: true,
        duration: `${duration}ms`,
        input: testPrompt,
        raw_response: responseData,
        parsed_content: JSON.parse(responseData.choices[0].message.content)
      };

      setTestResponse(JSON.stringify(result, null, 2));
      
      // Log to database
      await supabaseHelpers.logLLMProcessing({
        user_id: user.id,
        user_response: testPrompt,
        question_context: 'test_call',
        llm_model: 'gpt-4-turbo-preview',
        llm_response: JSON.stringify(responseData),
        processing_duration_ms: duration,
        extracted_tags: result.parsed_content.category_tags || [],
        extracted_google_types: result.parsed_content.google_places_categories || [],
        confidence_score: result.parsed_content.confidence_score || 0,
        success: true
      });

      // Refresh logs
      loadLLMLogs();

    } catch (error) {
      setTestResponse(`❌ Error: ${error.message}`);
      
      // Log error to database
      await supabaseHelpers.logLLMProcessing({
        user_id: user.id,
        user_response: testPrompt,
        question_context: 'test_call',
        processing_duration_ms: 0,
        success: false,
        error_message: error.message
      });
    } finally {
      setTestLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadLLMLogs();
    }
  }, [user]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="openai-debug-panel">
      <div className="panel-header">
        <h3>🤖 OpenAI API Debug</h3>
        <div className="debug-controls">
          <button onClick={testOpenAIAPI} disabled={testLoading} className="btn-debug btn-test">
            {testLoading ? '⏳ Testing...' : '🧪 Test API'}
          </button>
          <button onClick={loadLLMLogs} disabled={loading} className="btn-debug btn-refresh">
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="debug-info">
        <div className="info-row">
          <strong>Total LLM Calls:</strong> {llmLogs.length}
        </div>
        <div className="info-row">
          <strong>Successful Calls:</strong> {llmLogs.filter(log => log.success).length}
        </div>
        <div className="info-row">
          <strong>Failed Calls:</strong> {llmLogs.filter(log => !log.success).length}
        </div>
        <div className="info-row">
          <strong>API Key Status:</strong> {process.env.REACT_APP_OPENAI_API_KEY ? '✅ Configured' : '❌ Missing'}
        </div>
      </div>

      {testResponse && (
        <div className="test-response-section">
          <h4>Test API Response:</h4>
          <pre className="test-response">{testResponse}</pre>
        </div>
      )}

      <div className="llm-logs-section">
        <h4>Recent LLM Processing Logs:</h4>
        {loading ? (
          <div className="loading-state">Loading logs...</div>
        ) : (
          <div className="llm-logs-container">
            {llmLogs.map(log => (
              <div key={log.log_id} className={`llm-log-entry ${log.success ? 'success' : 'error'}`}>
                <div className="llm-log-header">
                  <span className="log-status">{log.success ? '✅' : '❌'}</span>
                  <span className="log-timestamp">{formatTimestamp(log.created_at)}</span>
                  <span className="log-duration">{log.processing_duration_ms}ms</span>
                  <span className="log-context">{log.question_context}</span>
                </div>
                <div className="llm-log-content">
                  <div className="user-response">
                    <strong>Input:</strong> {log.user_response}
                  </div>
                  {log.success ? (
                    <div className="extracted-data">
                      <div><strong>Tags:</strong> {(log.extracted_tags || []).join(', ')}</div>
                      <div><strong>Place Types:</strong> {(log.extracted_google_types || []).join(', ')}</div>
                      <div><strong>Confidence:</strong> {log.confidence_score}</div>
                    </div>
                  ) : (
                    <div className="error-message">
                      <strong>Error:</strong> {log.error_message}
                    </div>
                  )}
                  <details className="raw-response">
                    <summary>Raw Response</summary>
                    <pre>{log.llm_response}</pre>
                  </details>
                </div>
              </div>
            ))}
            {llmLogs.length === 0 && (
              <div className="empty-state">No LLM processing logs found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationDebugPanel = ({ user, supabaseHelpers }) => {
  const [debugData, setDebugData] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const runNotificationDiagnostic = async () => {
    if (!user) {
      alert('No user logged in');
      return;
    }

    setLoading(true);
    const timestamp = new Date().toISOString();
    
    try {
      console.log('🔔 Starting notification diagnostic...');
      console.log('🔔 Available supabaseHelpers:', supabaseHelpers);
      console.log('🔔 Function names:', supabaseHelpers ? Object.keys(supabaseHelpers) : 'undefined');
      
      // Test 1: Get unread count
      const unreadCountResult = await testUnreadCount();
      
      // Test 2: Get all notifications
      const notificationsResult = await testGetNotifications();
      
      // Test 3: Test direct database queries
      const databaseResult = await testDirectDatabase();
      
      // Test 4: Compare results
      const comparison = compareResults(unreadCountResult, notificationsResult, databaseResult);
      
      const finalDebugData = {
        timestamp,
        user: {
          id: user.sub,
          email: user.email
        },
        availableFunctions: supabaseHelpers ? Object.keys(supabaseHelpers) : [],
        tests: {
          unreadCount: unreadCountResult,
          notifications: notificationsResult,
          database: databaseResult
        },
        comparison,
        diagnosis: generateDiagnosis(unreadCountResult, notificationsResult, databaseResult)
      };
      
      setDebugData(finalDebugData);
      setLastRefresh(timestamp);
      
      console.log('🔔 Notification diagnostic complete:', finalDebugData);
      
    } catch (error) {
      console.error('Error in notification diagnostic:', error);
      setDebugData({
        timestamp,
        error: error.message,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const testUnreadCount = async () => {
    try {
      console.log('🔔 Testing unread count API...');
      
      if (!supabaseHelpers) {
        throw new Error('supabaseHelpers is not available');
      }
      
      if (!supabaseHelpers.getUnreadNotificationCount) {
        throw new Error('getUnreadNotificationCount function not found in supabaseHelpers');
      }
      
      const start = Date.now();
      const result = await supabaseHelpers.getUnreadNotificationCount(user.sub);
      const duration = Date.now() - start;
      
      console.log('🔔 Unread count result:', result);
      
      return {
        success: true,
        duration,
        data: result.data,
        error: result.error,
        rawResponse: result
      };
    } catch (error) {
      console.error('🔔 Unread count test failed:', error);
      return {
        success: false,
        error: error.message,
        stack: error.stack,
        availableFunctions: supabaseHelpers ? Object.keys(supabaseHelpers) : 'supabaseHelpers is undefined'
      };
    }
  };

  const testGetNotifications = async () => {
    try {
      console.log('🔔 Testing get notifications API...');
      
      if (!supabaseHelpers) {
        throw new Error('supabaseHelpers is not available');
      }
      
      if (!supabaseHelpers.getUserNotifications) {
        throw new Error('getUserNotifications function not found in supabaseHelpers');
      }
      
      const start = Date.now();
      const result = await supabaseHelpers.getUserNotifications(user.sub, 20);
      const duration = Date.now() - start;
      
      console.log('🔔 Get notifications result:', result);
      
      return {
        success: true,
        duration,
        data: result.data,
        error: result.error,
        count: result.data?.length || 0,
        unreadCount: result.data?.filter(n => !n.is_read).length || 0,
        rawResponse: result
      };
    } catch (error) {
      console.error('🔔 Get notifications test failed:', error);
      return {
        success: false,
        error: error.message,
        stack: error.stack,
        availableFunctions: supabaseHelpers ? Object.keys(supabaseHelpers) : 'supabaseHelpers is undefined'
      };
    }
  };

  const testDirectDatabase = async () => {
    try {
      console.log('🔔 Testing direct database queries...');
      const { supabase } = await import('../config/supabase');
      
      // Test 1: Raw notifications table
      const { data: allNotifications, error: allError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.sub)
        .order('created_at', { ascending: false });
      
      // Test 2: Unread notifications
      const { data: unreadNotifications, error: unreadError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.sub)
        .eq('is_read', false);
      
      // Test 3: Count queries
      const { count: totalCount, error: countError } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.sub);
      
      const { count: unreadDBCount, error: unreadCountError } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.sub)
        .eq('is_read', false);
      
      return {
        success: true,
        allNotifications: {
          data: allNotifications,
          error: allError,
          count: allNotifications?.length || 0
        },
        unreadNotifications: {
          data: unreadNotifications,
          error: unreadError,
          count: unreadNotifications?.length || 0
        },
        counts: {
          total: totalCount,
          unread: unreadDBCount,
          countError,
          unreadCountError
        }
      };
    } catch (error) {
      console.error('🔔 Direct database test failed:', error);
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  };

  const compareResults = (unreadCount, notifications, database) => {
    const apiUnreadCount = unreadCount.data || 0;
    const apiNotificationsCount = notifications.count || 0;
    const apiUnreadInList = notifications.unreadCount || 0;
    const dbTotalCount = database.allNotifications?.count || 0;
    const dbUnreadCount = database.unreadNotifications?.count || 0;
    const dbCountQuery = database.counts?.unread || 0;
    
    return {
      apiUnreadCount,
      apiNotificationsCount,
      apiUnreadInList,
      dbTotalCount,
      dbUnreadCount,
      dbCountQuery,
      discrepancies: {
        unreadCountVsApiList: apiUnreadCount !== apiUnreadInList,
        unreadCountVsDatabase: apiUnreadCount !== dbUnreadCount,
        unreadCountVsDBCount: apiUnreadCount !== dbCountQuery,
        apiListVsDatabase: apiNotificationsCount !== dbTotalCount
      }
    };
  };

  const generateDiagnosis = (unreadCount, notifications, database) => {
    const issues = [];
    const comparison = compareResults(unreadCount, notifications, database);
    
    if (!unreadCount.success) {
      issues.push('❌ Unread count API is failing');
    }
    
    if (!notifications.success) {
      issues.push('❌ Get notifications API is failing');
    }
    
    if (!database.success) {
      issues.push('❌ Direct database queries are failing');
    }
    
    if (comparison.discrepancies.unreadCountVsApiList) {
      issues.push(`🔍 Badge shows ${comparison.apiUnreadCount} but notification list has ${comparison.apiUnreadInList} unread`);
    }
    
    if (comparison.discrepancies.unreadCountVsDatabase) {
      issues.push(`🔍 Badge shows ${comparison.apiUnreadCount} but database has ${comparison.dbUnreadCount} unread`);
    }
    
    if (comparison.apiUnreadCount > 0 && comparison.apiNotificationsCount === 0) {
      issues.push('🎯 MAIN ISSUE: Badge shows notifications but list is empty');
    }
    
    if (issues.length === 0) {
      return ['✅ All notification systems are working correctly'];
    }
    
    return issues;
  };

  const copyDebugDataToClipboard = async () => {
    try {
      const dataToExport = {
        ...debugData,
        exportedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        appVersion: '1.0.0'
      };
      
      await navigator.clipboard.writeText(JSON.stringify(dataToExport, null, 2));
      alert('Notification debug data copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Check console for data.');
      console.log('Notification Debug Data:', debugData);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="notification-debug-panel">
      <div className="panel-header">
        <h3>🔔 Notification Debug</h3>
        <div className="debug-controls">
          <button onClick={runNotificationDiagnostic} disabled={loading} className="btn-debug">
            {loading ? '🔄' : '🔍'} Run Full Diagnostic
          </button>
          <button onClick={copyDebugDataToClipboard} disabled={!debugData.timestamp} className="btn-debug">
            📋 Copy JSON to Clipboard
          </button>
        </div>
      </div>
      
      <div className="debug-info">
        <div className="info-row">
          <strong>User:</strong> {user?.email || 'Not logged in'}
        </div>
        <div className="info-row">
          <strong>User ID:</strong> {user?.sub || 'N/A'}
        </div>
        <div className="info-row">
          <strong>Last Diagnostic:</strong> {lastRefresh ? formatTimestamp(lastRefresh) : 'Never'}
        </div>
        <div className="info-row">
          <strong>Available Functions:</strong> {debugData.availableFunctions ? debugData.availableFunctions.join(', ') : 'Not tested yet'}
        </div>
        <div className="info-row">
          <strong>Supabase Helpers:</strong> {supabaseHelpers ? '✅ Available' : '❌ Not available'}
        </div>
      </div>

      {debugData.error && (
        <div className="error-section">
          <h4>❌ Error</h4>
          <div className="error-message">{debugData.error}</div>
        </div>
      )}

      {debugData.diagnosis && (
        <div className="diagnosis-section">
          <h4>🩺 Diagnosis</h4>
          <div className="diagnosis-list">
            {debugData.diagnosis.map((issue, index) => (
              <div key={index} className={`diagnosis-item ${issue.startsWith('✅') ? 'success' : 'warning'}`}>
                {issue}
              </div>
            ))}
          </div>
        </div>
      )}

      {debugData.comparison && (
        <div className="comparison-section">
          <h4>📊 Data Comparison</h4>
          <div className="comparison-grid">
            <div className="comparison-item">
              <strong>API Unread Count:</strong> {debugData.comparison.apiUnreadCount}
            </div>
            <div className="comparison-item">
              <strong>API Notifications Total:</strong> {debugData.comparison.apiNotificationsCount}
            </div>
            <div className="comparison-item">
              <strong>API Unread in List:</strong> {debugData.comparison.apiUnreadInList}
            </div>
            <div className="comparison-item">
              <strong>DB Total Notifications:</strong> {debugData.comparison.dbTotalCount}
            </div>
            <div className="comparison-item">
              <strong>DB Unread Count:</strong> {debugData.comparison.dbUnreadCount}
            </div>
            <div className="comparison-item">
              <strong>DB Count Query:</strong> {debugData.comparison.dbCountQuery}
            </div>
          </div>
        </div>
      )}

      {debugData.tests && (
        <div className="tests-section">
          <h4>🧪 Test Results</h4>
          
          <div className="test-result">
            <h5>Unread Count API</h5>
            <div className={`test-status ${debugData.tests.unreadCount.success ? 'success' : 'error'}`}>
              {debugData.tests.unreadCount.success ? '✅' : '❌'} 
              {debugData.tests.unreadCount.success ? 'Success' : 'Failed'}
              {debugData.tests.unreadCount.duration && ` (${debugData.tests.unreadCount.duration}ms)`}
            </div>
            {debugData.tests.unreadCount.data !== undefined && (
              <div><strong>Count:</strong> {debugData.tests.unreadCount.data}</div>
            )}
            {debugData.tests.unreadCount.error && (
              <div className="error-details"><strong>Error:</strong> {debugData.tests.unreadCount.error}</div>
            )}
          </div>

          <div className="test-result">
            <h5>Get Notifications API</h5>
            <div className={`test-status ${debugData.tests.notifications.success ? 'success' : 'error'}`}>
              {debugData.tests.notifications.success ? '✅' : '❌'} 
              {debugData.tests.notifications.success ? 'Success' : 'Failed'}
              {debugData.tests.notifications.duration && ` (${debugData.tests.notifications.duration}ms)`}
            </div>
            {debugData.tests.notifications.count !== undefined && (
              <div><strong>Total:</strong> {debugData.tests.notifications.count}</div>
            )}
            {debugData.tests.notifications.unreadCount !== undefined && (
              <div><strong>Unread:</strong> {debugData.tests.notifications.unreadCount}</div>
            )}
            {debugData.tests.notifications.error && (
              <div className="error-details"><strong>Error:</strong> {debugData.tests.notifications.error}</div>
            )}
          </div>

          <div className="test-result">
            <h5>Direct Database Queries</h5>
            <div className={`test-status ${debugData.tests.database.success ? 'success' : 'error'}`}>
              {debugData.tests.database.success ? '✅' : '❌'} 
              {debugData.tests.database.success ? 'Success' : 'Failed'}
            </div>
            {debugData.tests.database.allNotifications && (
              <div><strong>DB All Notifications:</strong> {debugData.tests.database.allNotifications.count}</div>
            )}
            {debugData.tests.database.unreadNotifications && (
              <div><strong>DB Unread:</strong> {debugData.tests.database.unreadNotifications.count}</div>
            )}
            {debugData.tests.database.error && (
              <div className="error-details"><strong>Error:</strong> {debugData.tests.database.error}</div>
            )}
          </div>
        </div>
      )}

      {debugData.tests?.notifications?.data && debugData.tests.notifications.data.length > 0 && (
        <div className="notifications-preview">
          <h4>📱 Notifications Preview</h4>
          {debugData.tests.notifications.data.slice(0, 3).map((notification, index) => (
            <div key={notification.id || index} className="notification-preview">
              <div><strong>{notification.title}</strong></div>
              <div>{notification.message}</div>
              <div>
                <span className={`read-status ${notification.is_read ? 'read' : 'unread'}`}>
                  {notification.is_read ? '✅ Read' : '🔔 Unread'}
                </span>
                | Created: {formatTimestamp(notification.created_at)}
              </div>
            </div>
          ))}
          {debugData.tests.notifications.data.length > 3 && (
            <div>... and {debugData.tests.notifications.data.length - 3} more notifications</div>
          )}
        </div>
      )}
    </div>
  );
};

// Interests Debug Panel Component
const InterestsDebugPanel = ({ user }) => {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadInterests = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Access the global supabase integration
      const { supabase } = await import('../config/supabase');
      
      // Get ALL user interests from ALL users (for admin debug purposes)
      // Include both active and expired interests for comprehensive debugging
      const { data, error } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url, email),
          place:places!user_place_interests_place_id_fkey(id, google_place_id, name, address, location)
        `)
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error('Error loading interests for debug:', error);
      } else {
        console.log('🔍 DEBUG: Raw interests data from database:', data);
        console.log('🔍 DEBUG: Total interests found:', data?.length || 0);
        
        // Transform the data to include Google Place ID for compatibility
        const transformedData = data?.map(interest => ({
          ...interest,
          place_id: interest.place?.google_place_id, // Frontend expects Google Place ID
          google_place_id: interest.place?.google_place_id,
          place_uuid: interest.place?.id // Keep the UUID for internal use
        })) || [];
        
        console.log('🔍 DEBUG: Transformed interests data:', transformedData);
        console.log('🔍 DEBUG: Active interests:', transformedData.filter(i => i.is_currently_interested));
        console.log('🔍 DEBUG: Non-expired interests:', transformedData.filter(i => new Date(i.interest_expires_at) > new Date()));
        
        setInterests(transformedData);
        setLastRefresh(new Date().toISOString());
      }
    } catch (error) {
      console.error('Error in interests debug:', error);
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async () => {
    try {
      const { backgroundCleanup } = await import('../config/supabase');
      const result = await backgroundCleanup.runCleanup();
      console.log('Manual cleanup result:', result);
      // Refresh interests after cleanup
      setTimeout(loadInterests, 1000);
    } catch (error) {
      console.error('Error running manual cleanup:', error);
    }
  };

  useEffect(() => {
    loadInterests();
  }, [user]);

  useEffect(() => {
    if (!autoRefresh) return;
    
    // MOBILE-OPTIMIZED: Coordinated timing to prevent synchronization conflicts
    const isMobileDevice = window.innerWidth <= 768;
    
    if (isMobileDevice) {
      // Mobile: Use longer intervals with different offset from other components
      const mobileInterval = 25000; // 25 seconds
      const mobileOffset = 15000; // 15 second offset (different from NotificationBell's 8s)
      
      const initialTimeout = setTimeout(() => {
        const interval = setInterval(loadInterests, mobileInterval);
        return () => clearInterval(interval);
      }, mobileOffset);
      
      return () => clearTimeout(initialTimeout);
    } else {
      // Desktop: Standard 10 second polling
      const interval = setInterval(loadInterests, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, user]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const isExpired = (timestamp) => {
    if (!timestamp) return false;
    return new Date(timestamp) < new Date();
  };

  const getTimeUntilExpiry = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const expiry = new Date(timestamp);
    const diff = expiry - now;
    
    if (diff <= 0) return 'EXPIRED';
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="interests-debug-panel">
      <div className="panel-header">
        <h3>📍 User Interests Debug</h3>
        <div className="debug-controls">
          <button onClick={loadInterests} disabled={loading} className="btn-debug">
            {loading ? '🔄' : '🔄'} Refresh
          </button>
          <button onClick={runCleanup} className="btn-debug">
            🧹 Run Cleanup
          </button>
          <label className="auto-refresh-toggle">
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (10s)
          </label>
        </div>
      </div>
      
      <div className="debug-info">
        <div className="info-row">
          <strong>User:</strong> {user?.email || 'Not logged in'}
        </div>
        <div className="info-row">
          <strong>Last Refresh:</strong> {lastRefresh ? formatTime(lastRefresh) : 'Never'}
        </div>
        <div className="info-row">
          <strong>Total Interests:</strong> {interests.length}
        </div>
        <div className="info-row">
          <strong>Active Interests:</strong> {interests.filter(i => i.is_currently_interested && !isExpired(i.interest_expires_at)).length}
        </div>
        <div className="info-row">
          <strong>Expired Interests:</strong> {interests.filter(i => isExpired(i.interest_expires_at)).length}
        </div>
      </div>

      <div className="interests-list">
        <h4>All Interests (including expired)</h4>
        {interests.length === 0 ? (
          <div className="empty-state">No interests found</div>
        ) : (
          <div className="interests-table">
            <div className="table-header">
              <div>User</div>
              <div>Place</div>
              <div>Status</div>
              <div>Expires At</div>
              <div>Time Left</div>
              <div>Google ID</div>
            </div>
            {interests.map((interest, index) => (
              <div key={interest.id || index} className={`table-row ${isExpired(interest.interest_expires_at) ? 'expired' : 'active'}`}>
                <div className="user-info">
                  <div className="user-name">{interest.user?.display_name || interest.user?.full_name || interest.user?.username || 'Unknown User'}</div>
                  <div className="user-email">{interest.user?.email || 'No email'}</div>
                </div>
                <div className="place-info">
                  <div className="place-name">{interest.place_name || interest.place?.name || 'Unknown'}</div>
                  <div className="place-address">{interest.place_address || interest.place?.address || 'No address'}</div>
                </div>
                <div className={`status ${interest.is_currently_interested ? 'interested' : 'not-interested'}`}>
                  {interest.is_currently_interested ? '✅ Active' : '❌ Inactive'}
                </div>
                <div className="expires-at">
                  {formatTime(interest.interest_expires_at)}
                </div>
                <div className={`time-left ${isExpired(interest.interest_expires_at) ? 'expired' : ''}`}>
                  {getTimeUntilExpiry(interest.interest_expires_at)}
                </div>
                <div className="google-id">
                  {interest.google_place_id || interest.place_id || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PlaceInterestCountsDebug user={user} />
    </div>
  );
};

// Place Interest Counts Debug Component
const PlaceInterestCountsDebug = ({ user }) => {
  const [placeCounts, setPlaceCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadPlaceCounts = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { supabase } = await import('../config/supabase');
      
      // Get all places that have any interests (both active and expired for debugging)
      const { data: placesWithInterests, error: placesError } = await supabase
        .from('user_place_interests')
        .select(`
          place_id,
          place:places!user_place_interests_place_id_fkey(id, google_place_id, name, address)
        `);
      
      console.log('🔍 DEBUG: All places with any interests:', placesWithInterests);

      if (placesError) {
        console.error('Error loading places with interests:', placesError);
        return;
      }

      // Get unique places
      const uniquePlaces = {};
      placesWithInterests.forEach(item => {
        if (item.place?.google_place_id) {
          uniquePlaces[item.place.google_place_id] = item.place;
        }
      });

      console.log('🔍 DEBUG: Found places with interests:', Object.keys(uniquePlaces));

      // Get counts for each place (without privacy restrictions for admin debug)
      const countsData = {};
      
      for (const [googlePlaceId, place] of Object.entries(uniquePlaces)) {
        // Get all interests for this place (both active and expired for debugging)
        const { data: allInterests, error } = await supabase
          .from('user_place_interests')
          .select(`
            *,
            user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url, email, default_privacy_mode)
          `)
          .eq('place_id', place.id);
        
        console.log(`🔍 DEBUG: All interests for place ${place.name}:`, allInterests);

        if (!error && allInterests) {
          // Filter for active and non-expired interests
          const activeInterests = allInterests.filter(interest => 
            interest.is_currently_interested && 
            new Date(interest.interest_expires_at) > new Date()
          );
          
          // Count by privacy mode (for admin debug - show all)
          const counts = {
            total: allInterests.length,
            active: activeInterests.length,
            expired: allInterests.filter(interest => new Date(interest.interest_expires_at) <= new Date()).length,
            inactive: allInterests.filter(interest => !interest.is_currently_interested).length,
            byPrivacy: {},
            users: allInterests.map(interest => ({
              userId: interest.user_id,
              email: interest.user?.email,
              name: interest.user?.display_name || interest.user?.full_name,
              privacyMode: interest.user?.default_privacy_mode || 'public',
              expiresAt: interest.interest_expires_at,
              isActive: interest.is_currently_interested,
              isExpired: new Date(interest.interest_expires_at) <= new Date()
            }))
          };

          // Group by privacy mode (only active interests)
          activeInterests.forEach(interest => {
            const privacy = interest.user?.default_privacy_mode || 'public';
            counts.byPrivacy[privacy] = (counts.byPrivacy[privacy] || 0) + 1;
          });

          countsData[googlePlaceId] = {
            place: {
              googlePlaceId,
              name: place.name,
              address: place.address
            },
            counts
          };
        }
      }

      setPlaceCounts(countsData);
      setLastRefresh(new Date().toISOString());
      
    } catch (error) {
      console.error('Error loading place counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const runDirectDatabaseQuery = async () => {
    try {
      const { supabase } = await import('../config/supabase');
      
      console.log('🔍 DIRECT DB QUERY: Running comprehensive database check...');
      
      // Query 1: All user_place_interests records
      const { data: allInterests, error: interestsError } = await supabase
        .from('user_place_interests')
        .select('*')
        .order('created_at', { ascending: false });
      
      console.log('🔍 DIRECT DB QUERY: All user_place_interests records:', allInterests);
      console.log('🔍 DIRECT DB QUERY: Total records:', allInterests?.length || 0);
      
      // Query 2: All places
      const { data: allPlaces, error: placesError } = await supabase
        .from('places')
        .select('*');
      
      console.log('🔍 DIRECT DB QUERY: All places:', allPlaces);
      
      // Query 3: All profiles
      const { data: allProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');
      
      console.log('🔍 DIRECT DB QUERY: All profiles:', allProfiles);
      
      // Query 4: Join query like the debug panel uses
      const { data: joinedData, error: joinError } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url, email),
          place:places!user_place_interests_place_id_fkey(id, google_place_id, name, address, location)
        `)
        .order('created_at', { ascending: false });
      
      console.log('🔍 DIRECT DB QUERY: Joined data:', joinedData);
      
      if (interestsError) console.error('Interests query error:', interestsError);
      if (placesError) console.error('Places query error:', placesError);
      if (profilesError) console.error('Profiles query error:', profilesError);
      if (joinError) console.error('Join query error:', joinError);
      
    } catch (error) {
      console.error('🔍 DIRECT DB QUERY: Error:', error);
    }
  };

  useEffect(() => {
    loadPlaceCounts();
  }, [user]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="place-counts-debug-panel">
      <div className="panel-header">
        <h3>📊 Place Interest Counts Debug (No Privacy Restrictions)</h3>
        <div className="debug-controls">
          <button onClick={loadPlaceCounts} disabled={loading} className="btn-debug">
            {loading ? '🔄' : '🔄'} Refresh Counts
          </button>
          <button onClick={runDirectDatabaseQuery} className="btn-debug">
            🔍 Direct DB Query
          </button>
        </div>
      </div>
      
      <div className="debug-info">
        <div className="info-row">
          <strong>Last Refresh:</strong> {lastRefresh ? formatTime(lastRefresh) : 'Never'}
        </div>
        <div className="info-row">
          <strong>Places with Active Interests:</strong> {Object.keys(placeCounts).length}
        </div>
      </div>

      <div className="place-counts-list">
        {Object.keys(placeCounts).length === 0 ? (
          <div className="empty-state">No places with active interests found</div>
        ) : (
          <div className="place-counts-table">
            <div className="table-header">
              <div>Place</div>
              <div>Counts</div>
              <div>By Privacy (Active)</div>
              <div>Users</div>
              <div>Google ID</div>
            </div>
            {Object.entries(placeCounts).map(([googlePlaceId, data]) => (
              <div key={googlePlaceId} className="table-row">
                <div className="place-info">
                  <div className="place-name">{data.place.name || 'Unknown Place'}</div>
                  <div className="place-address">{data.place.address || 'No address'}</div>
                </div>
                <div className="total-count">
                  <div><strong>Total: {data.counts.total}</strong></div>
                  <div style={{color: '#38a169'}}>Active: {data.counts.active}</div>
                  <div style={{color: '#e53e3e'}}>Expired: {data.counts.expired}</div>
                  <div style={{color: '#888'}}>Inactive: {data.counts.inactive}</div>
                </div>
                <div className="privacy-breakdown">
                  {Object.entries(data.counts.byPrivacy).map(([privacy, count]) => (
                    <div key={privacy} className={`privacy-count privacy-${privacy}`}>
                      {privacy}: {count}
                    </div>
                  ))}
                </div>
                <div className="users-list">
                  {data.counts.users.map((user, index) => (
                    <div key={index} className={`user-item ${user.isExpired ? 'expired' : user.isActive ? 'active' : 'inactive'}`}>
                      <div className="user-email">{user.email}</div>
                      <div className="user-privacy">({user.privacyMode})</div>
                      <div className="user-status">
                        {user.isExpired ? '⏰ Expired' : user.isActive ? '✅ Active' : '❌ Inactive'}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="google-id">
                  {googlePlaceId}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Friends List Debug Panel Component
const FriendsListDebugPanel = ({ user, supabaseIntegration }) => {
  const [debugData, setDebugData] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const runFriendsListDiagnostic = async () => {
    if (!user) {
      setError('Missing user');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const results = {
        timestamp: new Date().toISOString(),
        user: {
          id: user.sub,
          email: user.email,
          name: user.name
        },
        steps: []
      };

      // Step 1: Get user's friendships
      console.log('🔍 FRIENDS DEBUG: Step 1 - Getting friendships for user:', user.sub);
      
      // Import Supabase client directly
      const { supabase } = await import('../config/supabase');
      
      try {
        const { data: friendships, error: friendshipsError } = await supabase
          .from('friendships')
          .select('*')
          .or(`requester_id.eq.${user.sub},friend_id.eq.${user.sub}`)
          .eq('status', 'accepted');

        results.steps.push({
          step: 1,
          name: 'Get Friendships',
          query: `SELECT * FROM friendships WHERE (requester_id = '${user.sub}' OR friend_id = '${user.sub}') AND status = 'accepted'`,
          success: !friendshipsError,
          error: friendshipsError?.message,
          data: friendships,
          count: friendships?.length || 0
        });

        if (friendshipsError) throw new Error(`Step 1 failed: ${friendshipsError.message}`);

        // Get friend user IDs
        const friendIds = friendships.map(friendship => 
          friendship.requester_id === user.sub ? friendship.friend_id : friendship.requester_id
        );
        results.friendIds = friendIds;

        console.log('🔍 FRIENDS DEBUG: Friend IDs found:', friendIds);

        if (friendIds.length === 0) {
          results.steps.push({
            step: 'COMPLETE',
            name: 'No friends found',
            message: 'User has no accepted friendships'
          });
          setDebugData(results);
          setLastRefresh(new Date().toISOString());
          return;
        }

        // Step 2: Get friend profiles
        console.log('🔍 FRIENDS DEBUG: Step 2 - Getting profiles for friends:', friendIds);
        try {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('*')
            .in('user_id', friendIds);

          results.steps.push({
            step: 2,
            name: 'Get Friend Profiles',
            query: `SELECT * FROM profiles WHERE user_id IN (${friendIds.map(id => `'${id}'`).join(', ')})`,
            success: !profilesError,
            error: profilesError?.message,
            data: profiles,
            count: profiles?.length || 0
          });

          if (profilesError) throw new Error(`Step 2 failed: ${profilesError.message}`);

          // Step 3: Get friends' current interests
          console.log('🔍 FRIENDS DEBUG: Step 3 - Getting current interests for friends');
          try {
            const { data: interests, error: interestsError } = await supabase
              .from('user_place_interests')
              .select(`
                *,
                places (
                  name,
                  address,
                  location,
                  rating,
                  price_level
                )
              `)
              .in('user_id', friendIds)
              .eq('is_currently_interested', true)
              .gt('interest_expires_at', new Date().toISOString());

            results.steps.push({
              step: 3,
              name: 'Get Current Interests',
              query: `SELECT * FROM user_place_interests WHERE user_id IN (...) AND is_currently_interested = true AND interest_expires_at > NOW()`,
              success: !interestsError,
              error: interestsError?.message,
              data: interests,
              count: interests?.length || 0
            });

            if (interestsError) throw new Error(`Step 3 failed: ${interestsError.message}`);

            // Step 4: Get friends' favorite places
            console.log('🔍 FRIENDS DEBUG: Step 4 - Getting favorite places for friends');
            try {
              const { data: favorites, error: favoritesError } = await supabase
                .from('user_place_interests')
                .select(`
                  *,
                  places (
                    name,
                    address,
                    location,
                    rating,
                    price_level
                  )
                `)
                .in('user_id', friendIds)
                .eq('is_favorite', true)
                .order('updated_at', { ascending: false })
                .limit(3);

              results.steps.push({
                step: 4,
                name: 'Get Favorite Places',
                query: `SELECT * FROM user_place_interests WHERE user_id IN (...) AND is_favorite = true ORDER BY updated_at DESC LIMIT 3`,
                success: !favoritesError,
                error: favoritesError?.message,
                data: favorites,
                count: favorites?.length || 0
              });

              if (favoritesError) throw new Error(`Step 4 failed: ${favoritesError.message}`);

              // Step 5: Combine data (same logic as FriendsList component)
              console.log('🔍 FRIENDS DEBUG: Step 5 - Combining data');
              const enrichedFriends = profiles.map(profile => ({
                ...profile,
                currentInterests: interests.filter(interest => interest.user_id === profile.user_id),
                favoriteSpots: favorites.filter(favorite => favorite.user_id === profile.user_id),
                lastSeen: profile.last_login_at,
                isActive: profile.last_login_at && 
                         (new Date() - new Date(profile.last_login_at)) < (24 * 60 * 60 * 1000)
              }));

              // Sort by activity status, then by last seen
              enrichedFriends.sort((a, b) => {
                if (a.isActive && !b.isActive) return -1;
                if (!a.isActive && b.isActive) return 1;
                if (a.lastSeen && b.lastSeen) {
                  return new Date(b.lastSeen) - new Date(a.lastSeen);
                }
                return 0;
              });

              results.steps.push({
                step: 5,
                name: 'Combine & Sort Data',
                success: true,
                data: enrichedFriends,
                count: enrichedFriends.length,
                details: {
                  activeFriends: enrichedFriends.filter(f => f.isActive).length,
                  friendsWithCurrentInterests: enrichedFriends.filter(f => f.currentInterests.length > 0).length,
                  friendsWithFavorites: enrichedFriends.filter(f => f.favoriteSpots.length > 0).length
                }
              });

              results.finalResult = enrichedFriends;
              results.success = true;

            } catch (error) {
              throw error;
            }
          } catch (error) {
            throw error;
          }
        } catch (error) {
          throw error;
        }
      } catch (error) {
        throw error;
      }

      setDebugData(results);
      setLastRefresh(new Date().toISOString());

    } catch (error) {
      console.error('🔍 FRIENDS DEBUG: Error during diagnostic:', error);
      setError(error.message);
      setDebugData(prev => ({
        ...prev,
        error: error.message,
        success: false
      }));
    } finally {
      setLoading(false);
    }
  };

  const copyDebugDataToClipboard = async () => {
    try {
      const dataToExport = {
        ...debugData,
        exportedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        appVersion: '1.0.0'
      };
      
      await navigator.clipboard.writeText(JSON.stringify(dataToExport, null, 2));
      alert('Friends list debug data copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Check console for data.');
      console.log('Friends List Debug Data:', debugData);
    }
  };

  const testIndividualQueries = async () => {
    if (!user) return;

    console.log('🔍 FRIENDS DEBUG: Testing individual database queries...');

    try {
      // Import Supabase client directly
      const { supabase } = await import('../config/supabase');

      // Test 1: Basic connection
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('user_id')
          .limit(1);
        console.log('✅ Database connection test:', { success: !error, data, error });
      } catch (error) {
        console.error('❌ Database connection test failed:', error);
      }

      // Test 2: Check if user has any friendships
      try {
        const { data, error } = await supabase
          .from('friendships')
          .select('*')
          .or(`requester_id.eq.${user.sub},friend_id.eq.${user.sub}`);
        console.log('✅ All friendships for user:', { data, error, count: data?.length });
      } catch (error) {
        console.error('❌ Friendships query failed:', error);
      }

      // Test 3: Check user's own profile
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.sub)
          .single();
        console.log('✅ User profile:', { data, error });
      } catch (error) {
        console.error('❌ User profile query failed:', error);
      }

      // Test 4: Check all profiles
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('user_id, display_name, full_name, last_login_at')
          .limit(10);
        console.log('✅ All profiles (sample):', { data, error, count: data?.length });
      } catch (error) {
        console.error('❌ All profiles query failed:', error);
      }
    } catch (importError) {
      console.error('❌ Failed to import Supabase client:', importError);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="friends-debug-panel">
      <div className="panel-header">
        <h3>🍽️ Friends List Debug</h3>
        <div className="debug-controls">
          <button onClick={runFriendsListDiagnostic} disabled={loading} className="btn-debug">
            {loading ? '🔄' : '🔍'} Run Full Diagnostic
          </button>
          <button onClick={testIndividualQueries} className="btn-debug">
            🧪 Test Individual Queries
          </button>
          <button onClick={copyDebugDataToClipboard} disabled={!debugData.timestamp} className="btn-debug">
            📋 Copy JSON to Clipboard
          </button>
        </div>
      </div>
      
      <div className="debug-info">
        <div className="info-row">
          <strong>User:</strong> {user?.email || 'Not logged in'}
        </div>
        <div className="info-row">
          <strong>User ID:</strong> {user?.sub || 'N/A'}
        </div>
        <div className="info-row">
          <strong>Last Diagnostic:</strong> {lastRefresh ? formatTimestamp(lastRefresh) : 'Never'}
        </div>
        <div className="info-row">
          <strong>Supabase Import:</strong> ✅ Using Direct Import
        </div>
      </div>

      {error && (
        <div className="error-section">
          <h4>❌ Error</h4>
          <div className="error-message">{error}</div>
        </div>
      )}

      {debugData.steps && (
        <div className="diagnostic-steps">
          <h4>Diagnostic Steps</h4>
          {debugData.steps.map((step, index) => (
            <div key={index} className={`diagnostic-step ${step.success ? 'success' : 'error'}`}>
              <div className="step-header">
                <span className="step-number">Step {step.step}</span>
                <span className="step-name">{step.name}</span>
                <span className="step-status">{step.success ? '✅' : '❌'}</span>
                {typeof step.count === 'number' && (
                  <span className="step-count">({step.count} records)</span>
                )}
              </div>
              
              {step.query && (
                <div className="step-query">
                  <strong>Query:</strong> <code>{step.query}</code>
                </div>
              )}
              
              {step.error && (
                <div className="step-error">
                  <strong>Error:</strong> {step.error}
                </div>
              )}

              {step.details && (
                <div className="step-details">
                  <strong>Details:</strong>
                  <ul>
                    {Object.entries(step.details).map(([key, value]) => (
                      <li key={key}>{key}: {value}</li>
                    ))}
                  </ul>
                </div>
              )}

              {step.data && step.data.length > 0 && (
                <div className="step-data">
                  <strong>Sample Data:</strong>
                  <pre>{JSON.stringify(step.data.slice(0, 2), null, 2)}</pre>
                  {step.data.length > 2 && <div>... and {step.data.length - 2} more records</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {debugData.finalResult && (
        <div className="final-result">
          <h4>Final Result</h4>
          <div className="result-summary">
            <div><strong>Total Friends:</strong> {debugData.finalResult.length}</div>
            <div><strong>Active Friends:</strong> {debugData.finalResult.filter(f => f.isActive).length}</div>
            <div><strong>Friends with Current Interests:</strong> {debugData.finalResult.filter(f => f.currentInterests.length > 0).length}</div>
            <div><strong>Friends with Favorites:</strong> {debugData.finalResult.filter(f => f.favoriteSpots.length > 0).length}</div>
          </div>
          
          <div className="friends-preview">
            <strong>Friends Preview:</strong>
            {debugData.finalResult.slice(0, 3).map((friend, index) => (
              <div key={index} className="friend-preview">
                <div><strong>{friend.display_name || friend.full_name}</strong> ({friend.user_id})</div>
                <div>Active: {friend.isActive ? '✅' : '❌'} | Interests: {friend.currentInterests.length} | Favorites: {friend.favoriteSpots.length}</div>
                <div>Last seen: {formatTimestamp(friend.lastSeen)}</div>
              </div>
            ))}
            {debugData.finalResult.length > 3 && <div>... and {debugData.finalResult.length - 3} more friends</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Add this new component before the AdminDebugPanel component
const DirectDatabaseInspector = ({ user }) => {
  const [dbData, setDbData] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState(null);

  const runDatabaseInspection = async () => {
    setLoading(true);
    try {
      const { supabase } = await import('../config/supabase');
      const results = {};
      
      // Query 1: All user_place_interests with joins
      try {
        const { data: interests, error: interestsError } = await supabase
          .from('user_place_interests')
          .select(`
            *,
            place:places!user_place_interests_place_id_fkey(id, google_place_id, name, address),
            user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url, email)
          `)
          .order('created_at', { ascending: false });
        
        if (interestsError) {
          results.interests = { error: interestsError.message };
        } else {
          // Transform the data for display
          results.interests = interests?.map(interest => ({
            ...interest,
            place_name: interest.place?.name,
            google_place_id: interest.place?.google_place_id,
            place_address: interest.place?.address,
            user_email: interest.user?.email,
            display_name: interest.user?.display_name,
            full_name: interest.user?.full_name
          })) || [];
        }
      } catch (error) {
        results.interests = { error: error.message };
      }

      // Query 2: All places
      try {
        const { data: places, error: placesError } = await supabase
          .from('places')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (placesError) {
          results.places = { error: placesError.message };
        } else {
          results.places = places || [];
        }
      } catch (error) {
        results.places = { error: error.message };
      }

      // Query 3: All profiles
      try {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, email, display_name, full_name, default_privacy_mode, created_at')
          .order('created_at', { ascending: false });
        
        if (profilesError) {
          results.profiles = { error: profilesError.message };
        } else {
          results.profiles = profiles || [];
        }
      } catch (error) {
        results.profiles = { error: error.message };
      }

      setDbData(results);
      setLastQuery(new Date().toISOString());
      
    } catch (error) {
      setDbData({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const isExpired = (timestamp) => {
    if (!timestamp) return false;
    return new Date(timestamp) < new Date();
  };

  const copyDataToClipboard = async () => {
    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        lastQuery: lastQuery,
        data: dbData,
        summary: {
          interests: Array.isArray(dbData.interests) ? dbData.interests.length : 'Error',
          places: Array.isArray(dbData.places) ? dbData.places.length : 'Error',
          profiles: Array.isArray(dbData.profiles) ? dbData.profiles.length : 'Error'
        }
      };
      
      await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
      
      // Show temporary success message
      const button = document.querySelector('.direct-db-inspector .btn-debug:nth-child(2)');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copied!';
        button.style.background = '#28a745';
        
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
        }, 2000);
      }
      
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      
      // Fallback: show data in console for manual copy
      console.log('DB Inspector Data Export:', JSON.stringify({
        timestamp: new Date().toISOString(),
        lastQuery: lastQuery,
        data: dbData
      }, null, 2));
      
      // Show error message
      const button = document.querySelector('.direct-db-inspector .btn-debug:nth-child(2)');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '❌ Failed - Check Console';
        button.style.background = '#dc3545';
        
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
        }, 3000);
      }
    }
  };

  return (
    <div className="direct-db-inspector">
      <div className="panel-header">
        <h3>🔍 Direct Database Inspector</h3>
        <div className="debug-controls">
          <button onClick={runDatabaseInspection} disabled={loading} className="btn-debug">
            {loading ? '⏳ Querying...' : '🔍 Inspect Database'}
          </button>
          <button onClick={copyDataToClipboard} disabled={!dbData || Object.keys(dbData).length === 0} className="btn-debug">
            📋 Copy JSON
          </button>
        </div>
      </div>
      
      {lastQuery && (
        <div className="debug-info">
          <div className="info-row">
            <strong>Last Query:</strong> {formatTimestamp(lastQuery)}
          </div>
        </div>
      )}

      {dbData.error && (
        <div className="error-section">
          <h4>❌ Error</h4>
          <pre>{dbData.error}</pre>
        </div>
      )}

      {dbData.interests && (
        <div className="db-section">
          <h4>📍 User Place Interests ({Array.isArray(dbData.interests) ? dbData.interests.length : 'Error'})</h4>
          {dbData.interests.error ? (
            <div className="error-message">{dbData.interests.error}</div>
          ) : Array.isArray(dbData.interests) ? (
            dbData.interests.length === 0 ? (
              <div className="empty-state">No interests found in database</div>
            ) : (
              <div className="db-table">
                <div className="table-header">
                  <div>User</div>
                  <div>Place</div>
                  <div>Status</div>
                  <div>Created</div>
                  <div>Expires</div>
                  <div>Google ID</div>
                </div>
                {dbData.interests.map((interest, index) => (
                  <div key={index} className={`table-row ${isExpired(interest.interest_expires_at) ? 'expired' : 'active'}`}>
                    <div className="user-info">
                      <div className="user-name">{interest.display_name || interest.full_name || 'Unknown'}</div>
                      <div className="user-email">{interest.user_email || 'No email'}</div>
                    </div>
                    <div className="place-info">
                      <div className="place-name">{interest.place_name || 'Unknown Place'}</div>
                      <div className="place-address">{interest.place_address || 'No address'}</div>
                    </div>
                    <div className={`status ${interest.is_currently_interested ? 'interested' : 'not-interested'}`}>
                      {interest.is_currently_interested ? '✅ Active' : '❌ Inactive'}
                    </div>
                    <div className="timestamp">
                      {formatTimestamp(interest.created_at)}
                    </div>
                    <div className={`timestamp ${isExpired(interest.interest_expires_at) ? 'expired' : ''}`}>
                      {formatTimestamp(interest.interest_expires_at)}
                    </div>
                    <div className="google-id">
                      {interest.google_place_id || 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="error-message">Invalid data format</div>
          )}
        </div>
      )}

      {dbData.places && (
        <div className="db-section">
          <h4>🏢 Places ({Array.isArray(dbData.places) ? dbData.places.length : 'Error'})</h4>
          {dbData.places.error ? (
            <div className="error-message">{dbData.places.error}</div>
          ) : Array.isArray(dbData.places) ? (
            dbData.places.length === 0 ? (
              <div className="empty-state">No places found in database</div>
            ) : (
              <div className="db-table">
                <div className="table-header">
                  <div>Name</div>
                  <div>Address</div>
                  <div>Google Place ID</div>
                  <div>Created</div>
                </div>
                {dbData.places.slice(0, 10).map((place, index) => (
                  <div key={index} className="table-row">
                    <div className="place-name">{place.name || 'Unknown'}</div>
                    <div className="place-address">{place.address || 'No address'}</div>
                    <div className="google-id">{place.google_place_id || 'N/A'}</div>
                    <div className="timestamp">{formatTimestamp(place.created_at)}</div>
                  </div>
                ))}
                {dbData.places.length > 10 && (
                  <div className="more-indicator">... and {dbData.places.length - 10} more places</div>
                )}
              </div>
            )
          ) : (
            <div className="error-message">Invalid data format</div>
          )}
        </div>
      )}

      {dbData.profiles && (
        <div className="db-section">
          <h4>👤 User Profiles ({Array.isArray(dbData.profiles) ? dbData.profiles.length : 'Error'})</h4>
          {dbData.profiles.error ? (
            <div className="error-message">{dbData.profiles.error}</div>
          ) : Array.isArray(dbData.profiles) ? (
            dbData.profiles.length === 0 ? (
              <div className="empty-state">No profiles found in database</div>
            ) : (
              <div className="db-table">
                <div className="table-header">
                  <div>Email</div>
                  <div>Display Name</div>
                  <div>Privacy Mode</div>
                  <div>User ID</div>
                </div>
                {dbData.profiles.map((profile, index) => (
                  <div key={index} className="table-row">
                    <div className="user-email">{profile.email || 'No email'}</div>
                    <div className="user-name">{profile.display_name || profile.full_name || 'No name'}</div>
                    <div className="privacy-mode">{profile.default_privacy_mode || 'public'}</div>
                    <div className="user-id">{profile.user_id || 'N/A'}</div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="error-message">Invalid data format</div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminDebugPanel = ({ user, profile, supabaseLoading, supabaseError, updateUserProfile, logUserEvent, getAllProfiles, getLogs, supabaseHelpers, DatabaseTestComponent, isOpen, onClose, onOpenFeedback }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [systemInfo, setSystemInfo] = useState({});
  const [apiCalls, setApiCalls] = useState([]);
  const [userActions, setUserActions] = useState([]);
  const [appState, setAppState] = useState({});

  // Initialize system info
  useEffect(() => {
    setSystemInfo({
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine
    });

    // Add initial log entry
    addLog('Admin Debug Panel initialized', 'system', {
      user: user?.email || 'Anonymous',
      timestamp: new Date().toISOString()
    });
  }, [user]);

  const addLog = (message, type = 'info', data = null) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      message,
      type, // 'info', 'warning', 'error', 'system', 'api', 'user'
      data
    };
    setLogs(prev => [logEntry, ...prev].slice(0, 100)); // Keep last 100 logs
  };

  const addApiCall = (url, method, request, response, duration) => {
    const apiCall = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      url,
      method,
      request,
      response,
      duration,
      status: response?.status || 'pending'
    };
    setApiCalls(prev => [apiCall, ...prev].slice(0, 50)); // Keep last 50 API calls
  };

  const addUserAction = (action, details) => {
    const userAction = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      action,
      details,
      user: user?.email || 'Anonymous'
    };
    setUserActions(prev => [userAction, ...prev].slice(0, 50)); // Keep last 50 actions
  };

  // Expose methods globally for other components to use
  useEffect(() => {
    window.adminDebug = {
      addLog,
      addApiCall,
      addUserAction,
      setAppState: (newState) => setAppState(prev => ({ ...prev, ...newState }))
    };

    return () => {
      delete window.adminDebug;
    };
  }, []);

  const clearLogs = () => {
    setLogs([]);
    addLog('Logs cleared by admin', 'system');
  };

  const exportLogs = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      logs,
      apiCalls,
      userActions,
      systemInfo,
      appState,
      user: user ? {
        email: user.email,
        name: user.name,
        id: user.sub
      } : null
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    addLog('Debug data exported', 'system');
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getLogTypeIcon = (type) => {
    switch (type) {
      case 'error': return '🔴';
      case 'warning': return '🟡';
      case 'api': return '🌐';
      case 'user': return '👤';
      case 'system': return '⚙️';
      default: return '💙';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-debug-overlay">
      <div className="admin-debug-panel">
        <div className="admin-debug-header">
          <div className="header-left">
            <h2>🛠️ Admin Debug Panel</h2>
            <div className="current-version-indicator">
              Currently Running: <span className="version-badge">V2 - Integrated</span>
            </div>
          </div>
          <div className="admin-debug-actions">
            <button onClick={onOpenFeedback} className="btn-debug btn-feedback">
              💬 Feedback
            </button>
            <button onClick={exportLogs} className="btn-debug btn-export">
              📁 Export
            </button>
            <button onClick={onClose} className="btn-debug btn-close">
              ✕
            </button>
          </div>
        </div>

        <div className="admin-debug-tabs">
          <button 
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 Logs ({logs.length})
          </button>
          <button 
            className={`tab ${activeTab === 'api' ? 'active' : ''}`}
            onClick={() => setActiveTab('api')}
          >
            🌐 API Calls ({apiCalls.length})
          </button>
          <button 
            className={`tab ${activeTab === 'user' ? 'active' : ''}`}
            onClick={() => setActiveTab('user')}
          >
            👤 User Actions ({userActions.length})
          </button>
          <button 
            className={`tab ${activeTab === 'state' ? 'active' : ''}`}
            onClick={() => setActiveTab('state')}
          >
            📊 App State
          </button>
          <button 
            className={`tab ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            ⚙️ System Info
          </button>
          <button 
            className={`tab ${activeTab === 'maps' ? 'active' : ''}`}
            onClick={() => setActiveTab('maps')}
          >
            🗺️ Maps Test
          </button>
          <button 
            className={`tab ${activeTab === 'interests' ? 'active' : ''}`}
            onClick={() => setActiveTab('interests')}
          >
            📍 Interests Debug
          </button>
          <button 
            className={`tab ${activeTab === 'database' ? 'active' : ''}`}
            onClick={() => setActiveTab('database')}
          >
            🗄️ Database Test
          </button>
          <button 
            className={`tab ${activeTab === 'inspector' ? 'active' : ''}`}
            onClick={() => setActiveTab('inspector')}
          >
            🔍 DB Inspector
          </button>
          <button 
            className={`tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            🍽️ Friends Debug
          </button>
          <button 
            className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            🔔 Notifications Debug
          </button>
          <button 
            className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            ⚡ Performance Monitor
          </button>
          <button 
            className={`tab ${activeTab === 'tracker' ? 'active' : ''}`}
            onClick={() => setActiveTab('tracker')}
          >
            🎯 Interest Tracker
          </button>
          <button 
            className={`tab ${activeTab === 'openai' ? 'active' : ''}`}
            onClick={() => setActiveTab('openai')}
          >
            🤖 OpenAI Debug
          </button>
        </div>

        <div className="admin-debug-content">
          {activeTab === 'logs' && (
            <div className="logs-panel">
              <div className="panel-header">
                <h3>Application Logs</h3>
                <button onClick={clearLogs} className="btn-debug btn-clear">
                  🗑️ Clear
                </button>
              </div>
              <div className="logs-container">
                {logs.map(log => (
                  <div key={log.id} className={`log-entry log-${log.type}`}>
                    <div className="log-header">
                      <span className="log-icon">{getLogTypeIcon(log.type)}</span>
                      <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                      <span className="log-type">{log.type.toUpperCase()}</span>
                    </div>
                    <div className="log-message">{log.message}</div>
                    {log.data && (
                      <details className="log-data">
                        <summary>View Data</summary>
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                ))}
                {logs.length === 0 && (
                  <div className="empty-state">No logs yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="api-panel">
              <div className="panel-header">
                <h3>API Calls</h3>
              </div>
              <div className="api-container">
                {apiCalls.map(call => (
                  <div key={call.id} className="api-entry">
                    <div className="api-header">
                      <span className={`api-method method-${call.method.toLowerCase()}`}>
                        {call.method}
                      </span>
                      <span className="api-url">{call.url}</span>
                      <span className="api-time">{formatTimestamp(call.timestamp)}</span>
                      <span className={`api-status status-${Math.floor(call.status / 100)}`}>
                        {call.status}
                      </span>
                    </div>
                    <div className="api-details">
                      <details>
                        <summary>Request</summary>
                        <pre>{JSON.stringify(call.request, null, 2)}</pre>
                      </details>
                      <details>
                        <summary>Response</summary>
                        <pre>{JSON.stringify(call.response, null, 2)}</pre>
                      </details>
                    </div>
                  </div>
                ))}
                {apiCalls.length === 0 && (
                  <div className="empty-state">No API calls yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'user' && (
            <div className="user-panel">
              <div className="panel-header">
                <h3>User Actions</h3>
              </div>
              <div className="user-container">
                {userActions.map(action => (
                  <div key={action.id} className="user-entry">
                    <div className="user-header">
                      <span className="user-icon">👤</span>
                      <span className="user-action">{action.action}</span>
                      <span className="user-time">{formatTimestamp(action.timestamp)}</span>
                    </div>
                    <div className="user-details">
                      <div className="user-user">User: {action.user}</div>
                      {action.details && (
                        <details>
                          <summary>Details</summary>
                          <pre>{JSON.stringify(action.details, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
                {userActions.length === 0 && (
                  <div className="empty-state">No user actions yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'state' && (
            <div className="state-panel">
              <div className="panel-header">
                <h3>Application State</h3>
              </div>
              <div className="state-container">
                <div className="state-section">
                  <h4>Current User</h4>
                  <pre>{JSON.stringify(user, null, 2)}</pre>
                </div>
                <div className="state-section">
                  <h4>App State</h4>
                  <pre>{JSON.stringify(appState, null, 2)}</pre>
                </div>
                <div className="state-section">
                  <h4>Local Storage</h4>
                  <pre>{JSON.stringify(Object.fromEntries(
                    Object.entries(localStorage).map(([k, v]) => [k, v])
                  ), null, 2)}</pre>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="system-panel">
              <div className="panel-header">
                <h3>System Information</h3>
              </div>
              <div className="system-container">
                {Object.entries(systemInfo).map(([key, value]) => (
                  <div key={key} className="system-field">
                    <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong>
                    <span>{value?.toString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'maps' && (
            <MapsTestPage />
          )}

          {activeTab === 'interests' && (
            <InterestsDebugPanel user={user} />
          )}

          {activeTab === 'database' && DatabaseTestComponent && (
            <div className="database-panel">
              <div className="panel-header">
                <h3>Database Test Suite</h3>
              </div>
              <div className="database-container">
                <DatabaseTestComponent user={user} />
              </div>
            </div>
          )}

          {activeTab === 'inspector' && (
            <div className="inspector-panel">
              <DirectDatabaseInspector user={user} />
            </div>
          )}

          {activeTab === 'friends' && (
            <div className="friends-panel">
              <FriendsListDebugPanel 
                user={user} 
                supabaseIntegration={{
                  supabaseHelpers: supabaseHelpers
                }} 
              />
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="notifications-panel">
              <NotificationDebugPanel 
                user={user} 
                supabaseHelpers={supabaseHelpers}
              />
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="performance-panel">
              <PerformanceDebugPanel />
            </div>
          )}

          {activeTab === 'tracker' && (
            <div className="tracker-panel">
              <ShowInterestTracker 
                user={user} 
                supabaseHelpers={supabaseHelpers}
              />
            </div>
          )}

          {activeTab === 'openai' && (
            <div className="openai-panel">
              <OpenAIDebugPanel 
                user={user} 
                supabaseHelpers={supabaseHelpers}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Show Interest Tracker Component
const ShowInterestTracker = ({ user, supabaseHelpers }) => {
  const [trackingData, setTrackingData] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [summary, setSummary] = useState({
    totalClicks: 0,
    successfulClicks: 0,
    failedClicks: 0,
    averageResponseTime: 0
  });

  // Set up global tracking function
  useEffect(() => {
    window.trackShowInterestClick = (clickData) => {
      const timestamp = new Date().toISOString();
      const trackingEntry = {
        id: Date.now() + Math.random(),
        timestamp,
        ...clickData
      };
      
      setTrackingData(prev => [trackingEntry, ...prev].slice(0, 100)); // Keep last 100 clicks
      
      // Update summary
      setSummary(prev => {
        const newTotal = prev.totalClicks + 1;
        const newSuccessful = clickData.success ? prev.successfulClicks + 1 : prev.successfulClicks;
        const newFailed = !clickData.success ? prev.failedClicks + 1 : prev.failedClicks;
        
        // Calculate new average response time
        const totalResponseTime = (prev.averageResponseTime * prev.totalClicks) + (clickData.responseTime || 0);
        const newAverageResponseTime = newTotal > 0 ? totalResponseTime / newTotal : 0;
        
        return {
          totalClicks: newTotal,
          successfulClicks: newSuccessful,
          failedClicks: newFailed,
          averageResponseTime: newAverageResponseTime
        };
      });
    };

    setIsTracking(true);

    return () => {
      delete window.trackShowInterestClick;
      setIsTracking(false);
    };
  }, []);

  const clearTrackingData = () => {
    setTrackingData([]);
    setSummary({
      totalClicks: 0,
      successfulClicks: 0,
      failedClicks: 0,
      averageResponseTime: 0
    });
  };

  const exportTrackingData = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      summary,
      trackingData,
      realTimeState: window.popmates_debug_realtime_interests || null,
      user: user ? {
        id: user.sub,
        email: user.email
      } : null
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `show-interest-tracking-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runManualTest = async () => {
    if (!user || !supabaseHelpers?.addUserInterest) {
      alert('Missing user or supabaseHelpers');
      return;
    }

    const testPlaceData = {
      place_id: 'ChIJTestPlace123',
      place_name: 'Manual Test Place',
      place_address: '123 Test Street',
      place_location: { lat: 37.7749, lng: -122.4194 }
    };

    const startTime = Date.now();
    
    try {
      const result = await supabaseHelpers.addUserInterest(user.sub, testPlaceData);
      const responseTime = Date.now() - startTime;
      
      window.trackShowInterestClick({
        actionType: 'manual_test',
        placeData: testPlaceData,
        success: !result.error,
        responseTime,
        result,
        error: result.error ? result.error.message : null
      });
      
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      window.trackShowInterestClick({
        actionType: 'manual_test',
        placeData: testPlaceData,
        success: false,
        responseTime,
        error: error.message
      });
    }
  };

  const getStatusSummary = () => {
    const realTimeState = window.popmates_debug_realtime_interests;
    return {
      tracking: isTracking ? '🟢 Active' : '🔴 Inactive',
      realTimeConnection: realTimeState?.isConnected ? '🟢 Connected' : '🔴 Disconnected',
      userInterests: realTimeState?.userInterests?.size || 0,
      placeInterestCounts: Object.keys(realTimeState?.placeInterestCounts || {}).length
    };
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const status = getStatusSummary();

  return (
    <div className="show-interest-tracker">
      <div className="panel-header">
        <h3>🎯 Show Interest Tracker</h3>
        <div className="debug-controls">
          <button onClick={runManualTest} className="btn-debug">
            🧪 Manual Test
          </button>
          <button onClick={clearTrackingData} className="btn-debug">
            🗑️ Clear Data
          </button>
          <button onClick={exportTrackingData} className="btn-debug">
            📁 Export JSON
          </button>
        </div>
      </div>
      
      <div className="tracker-status">
        <div className="status-grid">
          <div className="status-item">
            <strong>Tracking:</strong> {status.tracking}
          </div>
          <div className="status-item">
            <strong>Real-time:</strong> {status.realTimeConnection}
          </div>
          <div className="status-item">
            <strong>User Interests:</strong> {status.userInterests}
          </div>
          <div className="status-item">
            <strong>Place Counts:</strong> {status.placeInterestCounts}
          </div>
        </div>
      </div>

      <div className="tracker-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <div className="summary-number">{summary.totalClicks}</div>
            <div className="summary-label">Total Clicks</div>
          </div>
          <div className="summary-item success">
            <div className="summary-number">{summary.successfulClicks}</div>
            <div className="summary-label">Successful</div>
          </div>
          <div className="summary-item error">
            <div className="summary-number">{summary.failedClicks}</div>
            <div className="summary-label">Failed</div>
          </div>
          <div className="summary-item">
            <div className="summary-number">{Math.round(summary.averageResponseTime)}ms</div>
            <div className="summary-label">Avg Response</div>
          </div>
          <div className="summary-item">
            <div className="summary-number">{summary.totalClicks > 0 ? Math.round((summary.successfulClicks / summary.totalClicks) * 100) : 0}%</div>
            <div className="summary-label">Success Rate</div>
          </div>
        </div>
      </div>

      <div className="tracking-data">
        <h4>Click Tracking Data</h4>
        {trackingData.length === 0 ? (
          <div className="empty-state">
            No clicks tracked yet. Click a "Show Interest" button to start tracking.
          </div>
        ) : (
          <div className="tracking-table">
            <div className="table-header">
              <div>Time</div>
              <div>Action</div>
              <div>Place</div>
              <div>Status</div>
              <div>Response Time</div>
              <div>Details</div>
            </div>
            {trackingData.map((entry) => (
              <div key={entry.id} className={`table-row ${entry.success ? 'success' : 'error'}`}>
                <div className="timestamp">
                  {formatTimestamp(entry.timestamp)}
                </div>
                <div className="action-type">
                  {entry.actionType || 'show_interest'}
                </div>
                <div className="place-info">
                  <div className="place-name">{entry.placeData?.place_name || 'Unknown'}</div>
                  <div className="place-id">{entry.placeData?.place_id || 'N/A'}</div>
                </div>
                <div className={`status ${entry.success ? 'success' : 'error'}`}>
                  {entry.success ? '✅ Success' : '❌ Failed'}
                </div>
                <div className="response-time">
                  {entry.responseTime ? `${entry.responseTime}ms` : 'N/A'}
                </div>
                <div className="details">
                  {entry.error && (
                    <div className="error-message">
                      Error: {entry.error}
                    </div>
                  )}
                  {entry.result && (
                    <details>
                      <summary>View Result</summary>
                      <pre>{JSON.stringify(entry.result, null, 2)}</pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDebugPanel; 